import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
/**
 * UserMessageFade — renders a user message with per-character fade-in animation.
 *
 * Each character is wrapped in a `<span class="char-fade">` with staggered
 * `animation-delay`. Timing is adaptive: short messages animate at `baseCharDelayMs`
 * pace, longer messages speed up so the total animation never exceeds `maxTotalMs`.
 *
 * User messages are plain text (no markdown), so spans are rendered directly.
 * Newlines become `<br>` elements between character spans.
 */
export const UserMessageFade = memo(function UserMessageFade({ text, baseCharDelayMs = 20, maxTotalMs = 1500, fadeDurationMs = 150, }) {
    const prefersReducedMotion = usePrefersReducedMotion();
    const elements = useMemo(() => {
        if (prefersReducedMotion || text.length === 0) {
            // No animation — render plain text with line breaks
            return text.split('\n').flatMap((line, lineIdx, lines) => {
                const result = [_jsx("span", { children: line }, `line-${lineIdx}`)];
                if (lineIdx < lines.length - 1) {
                    result.push(_jsx("br", {}, `br-${lineIdx}`));
                }
                return result;
            });
        }
        const charDelayMs = Math.min(maxTotalMs / text.length, baseCharDelayMs);
        const nodes = [];
        let charIndex = 0;
        // Use Array.from to handle surrogate pairs (emoji) correctly
        const chars = Array.from(text);
        for (let i = 0; i < chars.length; i++) {
            const ch = chars[i];
            if (ch === '\n') {
                nodes.push(_jsx("br", {}, `br-${i}`));
            }
            else {
                nodes.push(_jsx("span", { className: "char-fade", "aria-hidden": "true", style: {
                        animationDuration: `${fadeDurationMs}ms`,
                        animationDelay: `${charIndex * charDelayMs}ms`,
                    }, children: ch }, `ch-${i}`));
                charIndex++;
            }
        }
        return nodes;
    }, [text, baseCharDelayMs, maxTotalMs, fadeDurationMs, prefersReducedMotion]);
    return (_jsx("span", { "aria-label": text, children: elements }));
});
//# sourceMappingURL=UserMessageFade.js.map